import React from 'react'
import { Box, IconButton, type SxProps, type Theme, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'

import { type IconName } from '~components/icons/iconMapping'
import { borderRadius, colors, lightTokens, spacing, typography } from '~tokens/internal'

import Icon from './Icon'
import { type SemanticIconName } from './icons/semanticIcons'

// Icon mapping for each banner type (component-specific, not a color token)
const bannerIconMap = {
  success: 'success',
  info: 'info',
  error: 'error',
  warning: 'warning',
  testMode: 'science',
  aiRecommendation: 'autoAwesome',
  upgrade: 'autoMode',
  feedback: 'campaign',
} as const

export type BannerType = keyof typeof bannerIconMap
export type BannerVariant = 'standard' | 'filled'

// Maps each banner type to its semantic color token
const bannerTokenMap: Record<BannerType, { main: string; lighter: string }> = {
  success: lightTokens.success,
  info: lightTokens.info,
  error: lightTokens.error,
  warning: lightTokens.warning,
  testMode: lightTokens.testMode,
  aiRecommendation: lightTokens.aiRecommendation,
  upgrade: lightTokens.upgrade,
  feedback: lightTokens.feedback,
}

// Static style objects extracted to module level to avoid re-creation on every render
const contentContainerSx = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
} as const

// Derive variant-specific styles from semantic color tokens
function getBannerStyles(
  type: BannerType,
  variant: BannerVariant,
): {
  bgColor: string
  textColor: string
  iconColor: string
} {
  const tokenColors = bannerTokenMap[type]

  switch (variant) {
    case 'filled':
      return {
        bgColor: tokenColors.main,
        textColor: lightTokens.primary.contrastText,
        iconColor: lightTokens.primary.contrastText,
      }
    case 'standard':
    default:
      return {
        bgColor: tokenColors.lighter,
        textColor: tokenColors.main,
        iconColor: tokenColors.main,
      }
  }
}

interface BannerProps {
  type?: BannerType
  variant?: BannerVariant
  title?: string
  body?: React.ReactNode | string
  dismissable?: boolean
  onDismiss?: () => void
  action?: React.ReactNode
  icon?: SemanticIconName | IconName
  sx?: SxProps<Theme>
}

const Banner: React.FC<BannerProps> = ({
  type = 'info',
  variant = 'standard',
  title,
  body,
  dismissable = false,
  onDismiss,
  action,
  icon,
  sx,
}) => {
  const [hideBanner, setHideBanner] = React.useState<boolean>(false)
  const iconName = icon ?? bannerIconMap[type]
  const styles = getBannerStyles(type, variant)

  const handleDismiss = (): void => {
    setHideBanner(true)
    onDismiss?.()
  }

  if (hideBanner) return null

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: title && body ? 'flex-start' : 'center',
        gap: spacing.xxs,
        p: spacing.compact,
        background: styles.bgColor,
        borderRadius: borderRadius.sm,
        width: '100%',
        ...sx,
      }}
    >
      <Box sx={{ color: styles.iconColor, display: 'flex', alignItems: 'center' }}>
        <Icon name={iconName} size={typography.fontSize.lg} />
      </Box>
      <Box sx={contentContainerSx}>
        {title && (
          <Typography
            sx={{
              color: styles.textColor,
              fontWeight: typography.fontWeight.semibold,
              fontSize: typography.fontSize.sm,
              lineHeight: typography.lineHeight.relaxed,
              letterSpacing: typography.letterSpacing.wide,
              mb: body ? spacing.micro : spacing.none,
            }}
          >
            {title}
          </Typography>
        )}
        {body && (
          <Typography
            sx={{
              color: styles.textColor,
              fontWeight: typography.fontWeight.regular,
              fontSize: typography.fontSize.xs,
              lineHeight: typography.lineHeight.normal,
              letterSpacing: typography.letterSpacing.wide,
            }}
          >
            {body}
          </Typography>
        )}
      </Box>
      {action && (
        <Box
          sx={{
            color: styles.textColor,
            '& .MuiButton-root': {
              color: styles.textColor,
              '&:hover': {
                backgroundColor: alpha(styles.iconColor, colors.opacity.hoverFill),
              },
            },
          }}
        >
          {React.Children.map(action, (child) =>
            React.isValidElement(child) ? React.cloneElement(child, { size: 'small' }) : child,
          )}
        </Box>
      )}
      {dismissable && (
        <IconButton size='small' onClick={handleDismiss} sx={{ color: styles.iconColor }}>
          <Icon name='close' size={typography.fontSize.md} />
        </IconButton>
      )}
    </Box>
  )
}

export default Banner
